import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { pdf } from '@react-pdf/renderer'

import { MainTitle } from '@generators/htmlComponents/MainTitle'
import { Line } from '@generators/htmlComponents/Line'
import Text from '@generators/htmlComponents/Text'
import { Signature } from '@generators/htmlComponents/Signature'
import Loading from '@components/loading'
import DownloadPdfButton from '@components/inputs/button/downloadPdfButton'
import ArrowBack from '@components/arrowBack'
import LoadingModal from '@components/loading/loadingModal'
import DoubleTextWithCard from '@generators/htmlComponents/DoubleTextWithCard'
import DoubleImage from '@generators/htmlComponents/DoubleImages'
import LoanInformedConsent from '@generators/loanInformedConsent'

import { getTermData as getTermDataAction } from '@stores/actions/loan'

import { cpfMask } from '@util/stringUtil'
import { getOnlyDateFromFormattedDateStr, isValidDate } from '@util/timeUtil'

import colors from '@settings/styleSettings'
import {
  Page, Document, BoldText, Section, ArrowArea
} from './styles'

const LoanConsentForm = ({ getTermData, termData }) => {
  const { loanId } = useParams()
  const [loadingModal, setLoadingModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    setLoadingModal(true)
    getTermData({ loanId }, () => {
      setLoading(false)
      setLoadingModal(false)
    })
  }, [loanId])

  if (!termData) return <Loading />

  const
    {
      beneficiaryName,
      cpf, generalImage, identificationImage, loanDate, loanLimitDate, assign, equipmentName

    } = termData

  const formattedDate = getOnlyDateFromFormattedDateStr(loanDate)
  const formattedLimitDate = getOnlyDateFromFormattedDateStr(loanLimitDate)
  const formattedCpf = cpfMask(cpf)
  const goBack = () => {
    navigate(-1)
  }

  const getReportCallback = async () => {
    const termsOfConsentBlob = await pdf(
      <LoanInformedConsent termData={termData} />
    )
    const termsOfConsentPdf = termsOfConsentBlob && (await termsOfConsentBlob.toBlob())
    const pdfWindow = window.open()
    pdfWindow.location.href = URL.createObjectURL(termsOfConsentPdf)
  }

  const onExportToPdf = () => {
    getReportCallback(termData)
  }

  return (
    <>
      <Document>
        <ArrowArea>
          <ArrowBack onClick={goBack} />
        </ArrowArea>
        <Page>
          <MainTitle
            title="Termo de empréstimo"
            topTitle={beneficiaryName}
            bottomTitle={`Termo Assinado em: ${formattedDate}`}
            topTitleColor={colors.strongGray}
            bottomTitleColor={colors.strongGray}
          />
          <Line lineColor={colors.softGray} />
          <Text style={{ color: colors.strongGray, fontSize: 14, textAlign: 'justify' }}>
            Eu
            <BoldText style={{ color: colors.strongGray }}>
              {' '}
              {beneficiaryName}
            </BoldText>
            ,
            portador(a) do CPF n°
            <BoldText style={{ color: colors.strongGray }}>
              {' '}
              {formattedCpf}
            </BoldText>
            , declaro que recebi e estou ciente das orientações abaixo.
            Desse modo, manifesto ainda para fins de responsabilidade,
            que recebi nesta data o equipamento citado abaixo em perfeitas condições,
            para fins de execução das atividades inerentes à organização,
            devendo zelar pela sua guarda,
            conservação e, se necessário, devolução ao final do contrato.
            Ademais, eu concordo com as seguintes condições:.
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 14, paddingTop: 10, textAlign: 'justify'
          }}
          >
            • Se o equipamento for danificado ou inutilizado por emprego inadequado,
            mau uso, negligência ou extravio, a empresa me fornecerá novo equipamento
            e cobrará o valor de mercado de um equipamento da mesma marca ou equivalente
            ao da praça
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 14, marginTop: 0, textAlign: 'justify'
          }}
          >
            • Em caso de dano, inutilização ou extravio do
            equipamento deverei comunicar imediatamente ao setor competente.
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 14, marginTop: 0, textAlign: 'justify'
          }}
          >
            • Terminando os serviços ou no caso de rescisão do contrato de serviço,
            devolverei o equipamento completo e em perfeito estado de conservação,
            considerando-se o tempo do uso dele ao setor competente
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 14, marginTop: 0, textAlign: 'justify'
          }}
          >
            • Estando os equipamentos em minha posse, estarei sujeito a inspeções sem prévio aviso.
          </Text>
          <Text style={{
            color: colors.strongGray, fontSize: 14, paddingTop: 10, textAlign: 'justify'
          }}
          >
            Posto isto, comprometo-me a substituí-lo em caso de perda ou estrago
            irreparável por idêntico ou similar.
          </Text>

          <DoubleTextWithCard
            title="Equipamento"
            text={equipmentName}
            title2="Data limite de devolução"
            text2={formattedLimitDate}
            showSecondCard={isValidDate(formattedLimitDate)}
          />
          <DoubleImage
            title="Visão Geral do Equipamento"
            image={generalImage}
            title2="Identificação do equipamento"
            image2={identificationImage}
          />
          <Section>
            <Signature image={assign} beneficiaryName={beneficiaryName} />
            <Line lineColor={colors.softGray} />
          </Section>
        </Page>
        {loading ? (
          <Loading />
        ) : (
          <DownloadPdfButton onClick={() => { onExportToPdf() }} />
        )}
      </Document>
      <LoadingModal isOpen={loadingModal} />
    </>
  )
}

LoanConsentForm.propTypes = {
  getTermData: PropTypes.func.isRequired,
  termData: PropTypes.object,
}

LoanConsentForm.defaultProps = {
  termData: {},
}
const mapStateToProps = ({ loan }) => ({
  termData: loan.termData,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getTermData: getTermDataAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(LoanConsentForm)
